import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/useToast';
import {
  collection,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  completeReferralPayout,
  rejectReferralPayout,
  syncMissingReferralEarnings,
} from '@/lib/referralSystem';
import {
  isReferralEligiblePaidRequest,
  referralWorkingsForRequest,
  earningMatchesRequest,
  roundMoney,
  isReferralPayoutDoc,
} from '@/lib/referralLedger';
import { formatCurrency } from '@/lib/currency';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Gift,
  RefreshCw,
} from 'lucide-react';
import { formatUkDateTime } from '@/lib/adminFormat';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = toDate(value);
  return d ? formatUkDateTime(d) : '—';
};

const fmt = (amount, currency = 'GBP') => formatCurrency(Number(amount) || 0, currency || 'GBP');

const artistName = (profilesMap, id) => {
  const p = profilesMap[id];
  if (!p) return id ? `${String(id).slice(0, 8)}…` : 'Unknown';
  return p.artist_name || p.karaoke_name || p.full_name || `${String(id).slice(0, 8)}…`;
};

const signupLabel = (referral) => {
  switch (referral?.signupStage) {
    case 'added_songs':
      return 'Building catalog';
    case 'gone_live':
      return 'Ready to earn';
    case 'completed_requests':
      return 'Fully active';
    default:
      return 'Signed up';
  }
};

const statusTone = (status) => {
  switch (status) {
    case 'pending':
    case 'requested':
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40';
    case 'approved':
    case 'paid':
    case 'active':
      return 'bg-green-500/20 text-green-300 border-green-500/40';
    case 'rejected':
      return 'bg-red-500/20 text-red-300 border-red-500/40';
    default:
      return 'bg-gray-500/20 text-gray-300 border-gray-500/40';
  }
};

const payoutMethodLabel = (payout) => {
  const method = String(payout?.paymentMethod || '').replace('_', ' ');
  return method || '—';
};

const payoutPayTo = (payout) => {
  const d = payout?.paymentDetails || {};
  if (payout?.paymentMethod === 'paypal' || d.email) {
    return d.email || 'PayPal (no email on file)';
  }
  if (d.accountNumber || d.sortCode || d.routingNumber) {
    const sort = d.sortCode || d.routingNumber;
    return `Acc ${d.accountNumber || '—'} · Sort ${sort || '—'}`;
  }
  return 'No payment details';
};

const formatTotalsMap = (totals) => {
  const entries = Object.entries(totals || {}).filter(([, v]) => Number(v) !== 0);
  if (!entries.length) return fmt(0, 'GBP');
  return entries.map(([ccy, amount]) => fmt(amount, ccy)).join(' · ');
};

const AdminReferralConsole = () => {
  const { isAdminAuthenticated } = useAdminAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [profilesMap, setProfilesMap] = useState({});
  const [requests, setRequests] = useState([]);
  const [expandedReferralId, setExpandedReferralId] = useState(null);
  const [expandedPayoutId, setExpandedPayoutId] = useState(null);
  const [expandedReferrerId, setExpandedReferrerId] = useState(null);
  const [activeTab, setActiveTab] = useState('payouts');

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [referralsSnap, earningsSnap, payoutsSnap, profilesSnap, requestsSnap] = await Promise.all([
        getDocs(collection(db, 'referrals')),
        getDocs(collection(db, 'earnings')),
        getDocs(collection(db, 'payouts')),
        getDocs(collection(db, 'profiles')),
        getDocs(collection(db, 'requests')),
      ]);

      const sortDesc = (a, b, field) => {
        const da = toDate(a[field])?.getTime() || 0;
        const dbTime = toDate(b[field])?.getTime() || 0;
        return dbTime - da;
      };

      setReferrals(
        referralsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => sortDesc(a, b, 'createdAt'))
      );
      setEarnings(
        earningsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => sortDesc(a, b, 'createdAt'))
      );
      setPayouts(
        payoutsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(isReferralPayoutDoc)
          .sort((a, b) => sortDesc(a, b, 'requestedAt'))
      );

      const map = {};
      profilesSnap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setProfilesMap(map);
      setRequests(requestsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading admin referral data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isAdminAuthenticated) {
      loadAllData();
    }
  }, [isAdminAuthenticated, loadAllData]);

  const referralByReferredId = useMemo(() => {
    const map = {};
    referrals.forEach((r) => {
      if (r.referredId && !map[r.referredId]) map[r.referredId] = r;
    });
    return map;
  }, [referrals]);

  const commissionLines = useMemo(() => {
    const referredIds = new Set(referrals.map((r) => r.referredId).filter(Boolean));
    return requests
      .filter((req) => referredIds.has(req.artist_id) && isReferralEligiblePaidRequest(req))
      .map((req) => {
        const referral = referralByReferredId[req.artist_id];
        const workings = referralWorkingsForRequest(req);
        const recorded = earnings.find((e) => earningMatchesRequest(e, req.id));
        return {
          requestId: req.id,
          songTitle: req.song_title || 'Song request',
          paidAt: req.paid_at || req.created_at || req.createdAt,
          referredId: req.artist_id,
          referrerId: referral?.referrerId || null,
          referralId: referral?.id || null,
          recordedEarningId: recorded?.id || null,
          recordedAmount: recorded ? Number(recorded.amount) || 0 : 0,
          recordedStatus: recorded?.status || null,
          missing: !recorded,
          ...workings,
        };
      })
      .filter((line) => line.referralId)
      .sort((a, b) => (toDate(b.paidAt)?.getTime() || 0) - (toDate(a.paidAt)?.getTime() || 0));
  }, [requests, referrals, referralByReferredId, earnings]);

  const missingLines = useMemo(() => {
    const recordedByReferred = {};
    earnings.forEach((earning) => {
      const id = earning.referredId;
      if (!id) return;
      recordedByReferred[id] = roundMoney((recordedByReferred[id] || 0) + (Number(earning.amount) || 0));
    });
    const computedByReferred = {};
    commissionLines.forEach((line) => {
      computedByReferred[line.referredId] = roundMoney(
        (computedByReferred[line.referredId] || 0) + line.referrer10
      );
    });
    return commissionLines.filter((line) => {
      if (!line.missing) return false;
      const recorded = recordedByReferred[line.referredId] || 0;
      const computed = computedByReferred[line.referredId] || 0;
      return recorded + 0.02 < computed;
    });
  }, [commissionLines, earnings]);

  const referrerRows = useMemo(() => {
    const byReferrer = {};
    referrals.forEach((referral) => {
      const id = referral.referrerId;
      if (!id) return;
      if (!byReferrer[id]) {
        byReferrer[id] = {
          referrerId: id,
          referrals: [],
          commission: {},
          recorded: {},
          pendingPayout: {},
          paidPayout: {},
        };
      }
      byReferrer[id].referrals.push(referral);
    });

    commissionLines.forEach((line) => {
      const row = byReferrer[line.referrerId];
      if (!row) return;
      const ccy = line.currency || 'GBP';
      row.commission[ccy] = roundMoney((row.commission[ccy] || 0) + line.referrer10);
    });

    earnings.forEach((earning) => {
      const row = byReferrer[earning.referrerId];
      if (!row) return;
      const ccy = String(earning.currency || 'GBP').toUpperCase();
      row.recorded[ccy] = roundMoney((row.recorded[ccy] || 0) + (Number(earning.amount) || 0));
    });

    payouts.forEach((payout) => {
      const row = byReferrer[payout.userId];
      if (!row) return;
      const ccy = String(payout.currency || 'GBP').toUpperCase();
      const amount = Number(payout.amount) || 0;
      if (payout.status === 'pending' || payout.status === 'approved') {
        row.pendingPayout[ccy] = roundMoney((row.pendingPayout[ccy] || 0) + amount);
      }
      if (payout.status === 'paid') {
        row.paidPayout[ccy] = roundMoney((row.paidPayout[ccy] || 0) + amount);
      }
    });

    return Object.values(byReferrer).sort((a, b) => {
      const aSum = Object.values(a.commission).reduce((s, n) => s + n, 0);
      const bSum = Object.values(b.commission).reduce((s, n) => s + n, 0);
      return bSum - aSum;
    });
  }, [referrals, commissionLines, earnings, payouts]);

  const pendingPayouts = useMemo(
    () => payouts.filter((p) => p.status === 'pending' || p.status === 'approved'),
    [payouts]
  );
  const completedPayouts = useMemo(
    () => payouts.filter((p) => p.status === 'paid'),
    [payouts]
  );
  const otherPayouts = useMemo(
    () => payouts.filter((p) => p.status !== 'pending' && p.status !== 'approved' && p.status !== 'paid'),
    [payouts]
  );

  const stats = useMemo(() => {
    const commission = {};
    commissionLines.forEach((line) => {
      const ccy = line.currency || 'GBP';
      commission[ccy] = roundMoney((commission[ccy] || 0) + line.referrer10);
    });
    const pending = {};
    pendingPayouts.forEach((p) => {
      const ccy = String(p.currency || 'GBP').toUpperCase();
      pending[ccy] = roundMoney((pending[ccy] || 0) + (Number(p.amount) || 0));
    });
    const paid = {};
    completedPayouts.forEach((p) => {
      const ccy = String(p.currency || 'GBP').toUpperCase();
      paid[ccy] = roundMoney((paid[ccy] || 0) + (Number(p.amount) || 0));
    });
    return {
      referralCount: referrals.length,
      referrerCount: referrerRows.length,
      commission,
      pending,
      paid,
      pendingCount: pendingPayouts.length,
      missingCount: missingLines.length,
    };
  }, [referrals, referrerRows, commissionLines, pendingPayouts, completedPayouts, missingLines]);

  const handleCompletePayout = async (payout) => {
    const name = artistName(profilesMap, payout.userId);
    const ccy = payout.currency || 'GBP';
    if (!window.confirm(`Mark ${fmt(payout.amount, ccy)} as paid to ${name}? This completes the referral payout.`)) {
      return;
    }
    try {
      setSavingId(payout.id);
      await completeReferralPayout(payout.id);
      toast({
        title: 'Payout marked completed',
        description: `${fmt(payout.amount, ccy)} to ${name} is now recorded as paid.`,
      });
      await loadAllData();
    } catch (error) {
      console.error('Error completing referral payout:', error);
      toast({
        title: 'Could not mark payout complete',
        description: error.message || 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleRejectPayout = async (payout) => {
    const name = artistName(profilesMap, payout.userId);
    if (!window.confirm(`Reject this referral payout request from ${name}? Their earnings will go back to pending.`)) {
      return;
    }
    try {
      setSavingId(payout.id);
      await rejectReferralPayout(payout.id);
      toast({
        title: 'Payout rejected',
        description: `${name}'s referral earnings are pending again.`,
      });
      await loadAllData();
    } catch (error) {
      console.error('Error rejecting referral payout:', error);
      toast({
        title: 'Could not reject payout',
        description: error.message || 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleSyncMissing = async () => {
    if (!missingLines.length) return;
    if (!window.confirm(
      `Record ${missingLines.length} missing referral earning${missingLines.length === 1 ? '' : 's'} from paid song requests? Tips stay excluded. This updates the artist's Referrals | Payouts ledger.`
    )) {
      return;
    }
    try {
      setSyncing(true);
      const created = await syncMissingReferralEarnings(missingLines);
      toast({
        title: 'Referral earnings recorded',
        description: created
          ? `${created} earning row${created === 1 ? '' : 's'} written from paid requests.`
          : 'No new rows were needed.',
      });
      await loadAllData();
    } catch (error) {
      console.error('Error syncing referral earnings:', error);
      toast({
        title: 'Could not record earnings',
        description: error.message || 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const payoutWorkingsCheck = (payout) => {
    const ccy = String(payout.currency || 'GBP').toUpperCase();
    const earned = commissionLines
      .filter((line) => line.referrerId === payout.userId && line.currency === ccy)
      .reduce((sum, line) => sum + line.referrer10, 0);
    const alreadyPaid = payouts
      .filter((p) => p.userId === payout.userId && p.status === 'paid' && String(p.currency || 'GBP').toUpperCase() === ccy)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const otherPending = payouts
      .filter((p) =>
        p.id !== payout.id
        && p.userId === payout.userId
        && (p.status === 'pending' || p.status === 'approved')
        && String(p.currency || 'GBP').toUpperCase() === ccy
      )
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const expectedAvailable = roundMoney(Math.max(0, earned - alreadyPaid - otherPending));
    const requested = roundMoney(Number(payout.amount) || 0);
    const matches = Math.abs(expectedAvailable - requested) < 0.02 || requested <= expectedAvailable + 0.02;
    return { earned: roundMoney(earned), alreadyPaid, expectedAvailable, requested, matches, ccy };
  };

  if (!isAdminAuthenticated) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading admin referral console...
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading referral data...</p>
        </div>
      </div>
    );
  }

  const renderPayoutTable = (rows, emptyLabel) => {
    if (!rows.length) {
      return <p className="text-gray-400 text-sm py-6 text-center">{emptyLabel}</p>;
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-gray-400">Requested</TableHead>
            <TableHead className="text-gray-400">Artist</TableHead>
            <TableHead className="text-gray-400">Amount</TableHead>
            <TableHead className="text-gray-400">Pay to</TableHead>
            <TableHead className="text-gray-400">Status</TableHead>
            <TableHead className="text-gray-400">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((payout) => {
            const expanded = expandedPayoutId === payout.id;
            const check = payoutWorkingsCheck(payout);
            const canAction = payout.status === 'pending' || payout.status === 'approved';
            return (
              <React.Fragment key={payout.id}>
                <TableRow
                  className="cursor-pointer hover:bg-gray-700/40"
                  onClick={() => setExpandedPayoutId(expanded ? null : payout.id)}
                >
                  <TableCell className="text-white">{formatDate(payout.requestedAt)}</TableCell>
                  <TableCell className="text-white">
                    <div className="flex items-center gap-2">
                      {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                      {artistName(profilesMap, payout.userId)}
                    </div>
                  </TableCell>
                  <TableCell className="text-green-400 font-medium">
                    {fmt(payout.amount, payout.currency)}
                  </TableCell>
                  <TableCell className="text-gray-300 text-sm">
                    <div className="capitalize">{payoutMethodLabel(payout)}</div>
                    <div className="text-gray-500 text-xs">{payoutPayTo(payout)}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`border ${statusTone(payout.status)}`}>
                      {payout.status === 'paid' ? 'completed' : payout.status}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {canAction ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={savingId === payout.id}
                          onClick={() => handleCompletePayout(payout)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Mark completed
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={savingId === payout.id}
                          onClick={() => handleRejectPayout(payout)}
                          className="text-red-400 border-red-500/50 hover:bg-red-600 hover:text-white"
                        >
                          Reject
                        </Button>
                      </div>
                    ) : payout.status === 'paid' ? (
                      <span className="text-gray-500 text-xs">
                        Paid {formatDate(payout.paidAt || payout.completedAt)}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-gray-900/50">
                      <div className="p-3 space-y-3 text-sm">
                        <div className={`rounded-md border p-3 ${check.matches ? 'border-green-700/50 bg-green-900/20' : 'border-amber-700/50 bg-amber-900/20'}`}>
                          <p className="text-white font-medium mb-1">Workings check</p>
                          <p className="text-gray-300">
                            10% of this artist’s referred paid songs: {fmt(check.earned, check.ccy)}
                            {' · '}already paid out: {fmt(check.alreadyPaid, check.ccy)}
                            {' · '}available: {fmt(check.expectedAvailable, check.ccy)}
                            {' · '}requested: {fmt(check.requested, check.ccy)}
                          </p>
                          <p className={`text-xs mt-1 ${check.matches ? 'text-green-400' : 'text-amber-300'}`}>
                            {check.matches
                              ? 'Requested amount is within the computed referral balance.'
                              : 'Requested amount does not match the computed balance — double-check before paying.'}
                          </p>
                        </div>
                        {(payout.earningsBreakdown || []).length > 0 && (
                          <div>
                            <p className="text-gray-400 mb-2">Included earnings</p>
                            {(payout.earningsBreakdown || []).map((row, idx) => (
                              <div key={row.earningId || idx} className="flex justify-between text-gray-300 py-1 border-b border-gray-800 last:border-0">
                                <span>
                                  {row.referredId ? artistName(profilesMap, row.referredId) : 'Referral'}
                                  {row.period ? ` · ${row.period}` : ''}
                                </span>
                                <span className="text-green-400">{fmt(row.amount, row.currency || payout.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">Referral earnings</h2>
          <p className="text-gray-400">
            Separate ledger from artist payouts. Tips stay with the performing artist.
          </p>
        </div>
        <Button onClick={loadAllData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4 text-sm text-gray-300 space-y-1">
          <p className="text-white font-medium">How a paid song request splits</p>
          <p>On a £100 paid song (no tip): referred artist <span className="text-white">£70</span> · YSR <span className="text-white">£20</span> · referrer <span className="text-green-400">£10</span>.</p>
          <p className="text-gray-500">Referral payouts are requested by the artist on Refer → Payouts. Mark them completed here after you have paid them.</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Referrals</p>
              <p className="text-2xl font-bold text-white">{stats.referralCount}</p>
              <p className="text-xs text-gray-500">{stats.referrerCount} referrers</p>
            </div>
            <Users className="h-8 w-8 text-blue-400" />
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Computed 10% earned</p>
              <p className="text-2xl font-bold text-white">{formatTotalsMap(stats.commission)}</p>
              <p className="text-xs text-gray-500">From paid songs, tips excluded</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-400" />
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Payouts requested</p>
              <p className="text-2xl font-bold text-yellow-300">{formatTotalsMap(stats.pending)}</p>
              <p className="text-xs text-gray-500">{stats.pendingCount} waiting</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-400" />
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Payouts completed</p>
              <p className="text-2xl font-bold text-white">{formatTotalsMap(stats.paid)}</p>
              {stats.missingCount > 0 && (
                <p className="text-xs text-amber-300">{stats.missingCount} songs not yet on the artist ledger</p>
              )}
            </div>
            <CheckCircle className="h-8 w-8 text-green-400" />
          </CardContent>
        </Card>
      </div>

      {stats.missingCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-600/40 bg-amber-900/20 p-4">
          <AlertCircle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-amber-100 font-medium">
              {stats.missingCount} paid song{stats.missingCount === 1 ? '' : 's'} have referrer commission that is not yet on the artist’s Referrals | Payouts ledger
            </p>
            <p className="text-amber-200/80 text-sm mt-1">
              The workings below are calculated from paid requests. Record them so the artist can request a payout.
            </p>
          </div>
          <Button
            onClick={handleSyncMissing}
            disabled={syncing}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
          >
            {syncing ? 'Recording…' : 'Record missing earnings'}
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-gray-800">
          <TabsTrigger value="payouts">
            Payouts{stats.pendingCount ? ` (${stats.pendingCount})` : ''}
          </TabsTrigger>
          <TabsTrigger value="referrers">Referrers</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="workings">Workings</TabsTrigger>
        </TabsList>

        <TabsContent value="payouts" className="space-y-4">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-400" />
                Requested from Refer → Payouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderPayoutTable(pendingPayouts, 'No referral payouts waiting. Artists request these on their Refer tab.')}
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderPayoutTable(completedPayouts, 'No completed referral payouts yet.')}
            </CardContent>
          </Card>

          {otherPayouts.length > 0 && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-400" />
                  Rejected / other
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderPayoutTable(otherPayouts, '')}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="referrers">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">How much each referrer has made</CardTitle>
            </CardHeader>
            <CardContent>
              {referrerRows.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No referrers yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-gray-400">Referrer</TableHead>
                      <TableHead className="text-gray-400">People referred</TableHead>
                      <TableHead className="text-gray-400">10% earned</TableHead>
                      <TableHead className="text-gray-400">Requested</TableHead>
                      <TableHead className="text-gray-400">Paid out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrerRows.map((row) => {
                      const expanded = expandedReferrerId === row.referrerId;
                      const theirLines = commissionLines.filter((l) => l.referrerId === row.referrerId);
                      return (
                        <React.Fragment key={row.referrerId}>
                          <TableRow
                            className="cursor-pointer hover:bg-gray-700/40"
                            onClick={() => setExpandedReferrerId(expanded ? null : row.referrerId)}
                          >
                            <TableCell className="text-white">
                              <div className="flex items-center gap-2">
                                {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                                {artistName(profilesMap, row.referrerId)}
                              </div>
                            </TableCell>
                            <TableCell className="text-white">{row.referrals.length}</TableCell>
                            <TableCell className="text-green-400">{formatTotalsMap(row.commission)}</TableCell>
                            <TableCell className="text-yellow-300">{formatTotalsMap(row.pendingPayout)}</TableCell>
                            <TableCell className="text-gray-300">{formatTotalsMap(row.paidPayout)}</TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-gray-900/50">
                                <div className="p-3 space-y-2">
                                  {row.referrals.map((referral) => {
                                    const lines = theirLines.filter((l) => l.referredId === referral.referredId);
                                    const earned = {};
                                    lines.forEach((l) => {
                                      earned[l.currency] = roundMoney((earned[l.currency] || 0) + l.referrer10);
                                    });
                                    return (
                                      <div key={referral.id} className="flex justify-between text-sm border-b border-gray-800 py-2 last:border-0">
                                        <div>
                                          <p className="text-white">{referral.referredStageName || artistName(profilesMap, referral.referredId)}</p>
                                          <p className="text-gray-500 text-xs">
                                            {signupLabel(referral)} · {lines.length} paid song{lines.length === 1 ? '' : 's'}
                                          </p>
                                        </div>
                                        <p className="text-green-400">{formatTotalsMap(earned)}</p>
                                      </div>
                                    );
                                  })}
                                  {row.referrals.length === 0 && (
                                    <p className="text-gray-500 text-sm">No referred artists yet</p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Who referred whom</CardTitle>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">No referrals found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-gray-400">Referrer</TableHead>
                      <TableHead className="text-gray-400">Referred artist</TableHead>
                      <TableHead className="text-gray-400">Code</TableHead>
                      <TableHead className="text-gray-400">Joined</TableHead>
                      <TableHead className="text-gray-400">Stage</TableHead>
                      <TableHead className="text-gray-400">10% earned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((referral) => {
                      const expanded = expandedReferralId === referral.id;
                      const lines = commissionLines.filter((l) => l.referralId === referral.id);
                      const earned = {};
                      lines.forEach((l) => {
                        earned[l.currency] = roundMoney((earned[l.currency] || 0) + l.referrer10);
                      });
                      return (
                        <React.Fragment key={referral.id}>
                          <TableRow
                            className="cursor-pointer hover:bg-gray-700/40"
                            onClick={() => setExpandedReferralId(expanded ? null : referral.id)}
                          >
                            <TableCell className="text-white">
                              <div className="flex items-center gap-2">
                                {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                                {artistName(profilesMap, referral.referrerId)}
                              </div>
                            </TableCell>
                            <TableCell className="text-white">
                              {referral.referredStageName || artistName(profilesMap, referral.referredId)}
                            </TableCell>
                            <TableCell className="text-gray-400 font-mono">{referral.referralCode || '—'}</TableCell>
                            <TableCell className="text-gray-400">{formatDate(referral.createdAt)}</TableCell>
                            <TableCell>
                              <Badge className={`border ${statusTone(referral.signupStage === 'completed_requests' ? 'active' : 'pending')}`}>
                                {signupLabel(referral)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-green-400">{formatTotalsMap(earned)}</TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-gray-900/50">
                                <div className="p-3 space-y-2">
                                  {lines.length === 0 ? (
                                    <p className="text-gray-500 text-sm">No paid song requests from this artist yet (tips never count).</p>
                                  ) : (
                                    lines.map((line) => (
                                      <div key={line.requestId} className="text-sm border-b border-gray-800 py-2 last:border-0">
                                        <div className="flex justify-between">
                                          <span className="text-white">{line.songTitle}</span>
                                          <span className="text-green-400">{fmt(line.referrer10, line.currency)} to referrer</span>
                                        </div>
                                        <p className="text-gray-500 text-xs mt-1">
                                          {formatDate(line.paidAt)} · Song {fmt(line.songGross, line.currency)}
                                          {line.tipExcluded > 0 ? ` · tip ${fmt(line.tipExcluded, line.currency)} excluded` : ''}
                                          {' → '}artist {fmt(line.referredArtist70, line.currency)}
                                          {' / '}YSR {fmt(line.platform20, line.currency)}
                                          {' / '}referrer {fmt(line.referrer10, line.currency)}
                                          {line.missing ? ' · not yet on artist ledger' : ` · ${line.recordedStatus || 'recorded'}`}
                                        </p>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workings">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-400" />
                Double-check each paid song
              </CardTitle>
            </CardHeader>
            <CardContent>
              {commissionLines.length === 0 ? (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-400">No paid song requests from referred artists yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-gray-400">When</TableHead>
                      <TableHead className="text-gray-400">Song</TableHead>
                      <TableHead className="text-gray-400">Referred artist</TableHead>
                      <TableHead className="text-gray-400">Referrer</TableHead>
                      <TableHead className="text-gray-400">Song (no tip)</TableHead>
                      <TableHead className="text-gray-400">70 / 20 / 10</TableHead>
                      <TableHead className="text-gray-400">Ledger</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionLines.map((line) => (
                      <TableRow key={line.requestId}>
                        <TableCell className="text-gray-300 text-sm">{formatDate(line.paidAt)}</TableCell>
                        <TableCell className="text-white">{line.songTitle}</TableCell>
                        <TableCell className="text-white">{artistName(profilesMap, line.referredId)}</TableCell>
                        <TableCell className="text-white">{artistName(profilesMap, line.referrerId)}</TableCell>
                        <TableCell className="text-white">
                          {fmt(line.songGross, line.currency)}
                          {line.tipExcluded > 0 && (
                            <span className="block text-xs text-gray-500">
                              tip {fmt(line.tipExcluded, line.currency)} kept by artist
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-300 text-sm">
                          {fmt(line.referredArtist70, line.currency)}
                          {' / '}
                          {fmt(line.platform20, line.currency)}
                          {' / '}
                          <span className="text-green-400">{fmt(line.referrer10, line.currency)}</span>
                        </TableCell>
                        <TableCell>
                          {line.missing && missingLines.some((m) => m.requestId === line.requestId) ? (
                            <Badge className="border bg-amber-500/20 text-amber-300 border-amber-500/40">Missing</Badge>
                          ) : line.missing ? (
                            <Badge className="border bg-gray-500/20 text-gray-300 border-gray-500/40">Covered</Badge>
                          ) : (
                            <Badge className={`border ${statusTone(line.recordedStatus)}`}>
                              {line.recordedStatus || 'recorded'}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReferralConsole;
